import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function SwissLuxuryTravelSkiButlerPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Swiss Ultra-Luxury Travel Boom: Private Ski Butlers' }
  ];

  const article = {
    category: 'Ultra-Wealthy Market & Specialized Services',
    date: 'March 3, 2025',
    title: 'Swiss Ultra-Luxury Travel Boom: Private Ski Butlers',
    subtitle: 'Economic Impact of Butler Services in Ultra-Wealthy Market',
    heroImage: '/images/generated/jba-fs-124-news_swiss-luxury-travel-ski-butler.webp',
    lead: 'In the Swiss Alps, an ultra-luxury travel service boom featuring private ski butlers, live-in chefs, and rare cigar collections has arrived.\n\nSwiss hotels recorded 42.8 million overnight stays in 2024—the highest in over 50 years—with five-star hotel guests accounting for 25-30% of total tourism revenue, demonstrating significant economic impact.',
    comment: 'The emergence of highly specialized services like private ski butlers in the Swiss Alps is deeply noted by our association as an important case showing the evolution and diversification of butler services.\n\nParticularly, the fact that butler services in the ultra-wealthy market have an economic impact of 25-30% of tourism revenue clearly demonstrates the value of dedicated butler service as the ultimate in personalization.\n\nThe economic importance of the butler profession being expressed in such concrete numbers proves butler services\' economic significance.\n\nThere is potential for introducing such specialized butler services in Japan\'s luxury resort market as well, and from the perspective of fusing traditional hospitality with modern luxury, we will actively work toward developing specialized butler services according to seasons and regional characteristics, and training butlers with advanced expertise capable of meeting ultra-wealthy needs.',
    related: [
      { to: '/en/news/raffles-sentosa-butler-package', image: '/images/generated/jba-fs-en-020-news_swiss-luxury-travel-ski-butler.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles Sentosa "The Butler Did It" Package', title: 'Raffles Sentosa "The Butler Did It" Package', desc: 'Ultimate bespoke luxury experience at $5,000 per night' },
      { to: '/en/news/ai-hospitality-revolution', image: '/images/generated/seq-ai-hospitality-related2-10a4f8.webp', aspect: 'aspect-[4/3]', alt: 'Related article: smart hotel room illustrating AI hospitality transformation', title: 'AI Hospitality Revolution: 5 Transformations', desc: 'The future of butler services opened by AI' }
    ]
  };

  const sources = [
    { label: 'Source', value: 'Euronews', link: false },
    { label: 'Article URL', value: 'https://www.euronews.com/travel/2025/03/03/private-ski-butlers-live-in-chefs-and-rare-cigars-inside-switzerlands-ultra-luxury-travel-', link: true },
    { label: 'Rare Cigars (The Chedi Andermatt)', value: 'https://www.thechediandermatt.com/dining/the-cigar-library', link: true },
    { label: 'Chalet Rates', value: 'https://www.firefly-collection.com/luxury-ski-chalets/luxury-catered-ski-chalets/', link: true },
    { label: 'Private Chef Pricing', value: 'https://adam-brunet.com/en/chalet-chef', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];

  const toc = [
    { id: 's-market', label: 'Market Overview and Record Growth' },
    { id: 's-vip', label: 'Private Ski Butlers and VIP Services' },
    { id: 's-chalet', label: 'Private Chalet Weekly Rates' },
    { id: 's-fivestar', label: 'Five-Star Accommodation Standards' },
    { id: 's-chef', label: 'Live-in Chef Pricing' },
    { id: 's-econ', label: 'Economic Impact of Luxury Tourism' },
    { id: 's-diff', label: 'Differentiation and Competitive Comparison' }
  ];

  return (
    <Layout>
      <title>Swiss Ultra-Luxury Travel Boom: Private Ski Butlers | Japan Butler Association</title>
      <meta name="description" content="Swiss hotels recorded 42.8 million overnight stays in 2024, with five-star guests driving 25-30% of tourism revenue amid a boom in private ski butlers, live-in chefs and rare cigars." />
      <link rel="canonical" href="https://butler-association.jp/en/news/swiss-luxury-travel-ski-butler" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/swiss-luxury-travel-ski-butler" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/swiss-luxury-travel-ski-butler" />

      <ArticleHero eyebrow="News / Luxury Market" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <div className="space-y-4">
              {article.lead.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).map((s, i) => (
                <p key={i} className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{s}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'In 2024, Swiss hotels recorded 42.8 million overnight stays, the highest in over 50 years, driven by luxury travelers including record visitor numbers from the United States',
                'Five-star hotels account for only about 8% of total overnight stays, yet their guests make up 25-30% of all Swiss tourism revenue',
                'Across Alpine destinations such as St. Moritz, Zermatt and Gstaad, highly personalized ski butler services—from pre-warming ski boots to heli-skiing—are on offer'
              ]}
            />

            {/* 01: Market (existing body, unchanged) */}
            <SectionHeading id="s-market" en="Market">Market Overview and Record Growth</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>With the global wealthy's business thriving, Swiss tourism is also experiencing rapid growth. In 2024, Swiss hotels recorded 42.8 million overnight stays, the highest in over 50 years.</p>
                <p className={P}>The main driver of this growth is luxury travelers, boasting record visitor numbers from the United States.</p>
              </div>
              <p className={P}>According to the World Economic Forum, global billionaires collectively increased their wealth by €1.94 trillion in 2024, with this wealth increase directly contributing to the expansion of Switzerland's ultra-luxury travel market.</p>
            </div>
            <SourceShot
              src="/images/news-sources/swiss-luxury-travel-ski-butler-euronews.jpg"
              alt="Screenshot of the Euronews article covering Switzerland's ultra-luxury travel trend and private ski butlers"
              media="Euronews"
              url="https://www.euronews.com/travel/2025/03/03/private-ski-butlers-live-in-chefs-and-rare-cigars-inside-switzerlands-ultra-luxury-travel-"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Five-star hotels are about 8% of overnight stays, yet their guests drive 25-30% of tourism revenue." cite="Euronews / Switzerland Tourism" />
            </div>

            {/* 02: Ski Butlers (existing body, unchanged) */}
            <SectionHeading id="s-vip" en="Ski Butlers">Private Ski Butlers and VIP Services</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Post-pandemic, Switzerland has further focused on personalized luxury experiences "customized for the few." Hotels in Alpine destinations like St. Moritz, Zermatt, and Gstaad are raising the bar with innovative services.</p>
              <p className={P}>Particularly notable are highly personalized services beyond conventional concepts, such as private ski butlers pre-warming ski boots, heli-skiing tours, and guest Harley-Davidson motorcycles.</p>
            </div>

            {/* Augmented: Chalet Rentals */}
            <SectionHeading id="s-chalet" en="Chalet Rentals">Private Chalet Weekly Rates</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Luxury ski chalets in Switzerland are listed by rental specialists from <Stat>€150,000</Stat> to <Stat>€350,000</Stat> per week. Properties in Verbier and Zermatt typically include ski-in/ski-out access and private chef services as standard.</p>
              <p className={P}>Equipped with ski butlers and live-in staff, these whole-chalet rentals are a favored stay format among the ultra-wealthy.</p>
            </div>

            {/* 03: Five-Star (existing body, unchanged) */}
            <SectionHeading id="s-fivestar" en="Five-Star">Five-Star Accommodation Luxury Standard Equipment</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Swiss five-star accommodations provide live-in chefs and concierge services curating once-in-a-lifetime experiences as standard equipment.</p>
              <div className="space-y-5">
                <p className={P}>Particularly impressive is The Chedi's collection of thousands of rare cigars, including cigars priced up to €8,600 (approximately $9,000).</p>
                <p className={P}>Such extremely specialized and expensive services are provided as standards meeting ultra-wealthy expectations.</p>
              </div>
            </div>
            <SourceShot
              src="/images/news-sources/swiss-luxury-travel-ski-butler-thechedi.jpg"
              alt="Screenshot of The Chedi Andermatt official website page for The Cigar Library, presenting its rare cigar collection"
              media="The Chedi Andermatt official site"
              url="https://www.thechediandermatt.com/dining/the-cigar-library"
            />

            {/* Augmented: Live-in Chefs */}
            <SectionHeading id="s-chef" en="Live-in Chefs">Live-in Chef Pricing</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Private chefs for chalet guests in the Alps start, by published rates, at around <Stat>€400</Stat> per day. Packages including breakfast and dinner average around <Stat>€2,800</Stat> per week.</p>
              <p className={P}>Such live-in and daily chef services, alongside ski butlers, are a core element supporting the personalization of a chalet stay.</p>
            </div>

            {/* 04: Economic Impact (existing body, unchanged) */}
            <SectionHeading id="s-econ" en="Economic Impact">Economic Impact of Luxury Tourism</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>For Switzerland, the wealthy visitor strategy is paying significant dividends. Foreign overnight stays in 2024 increased 5.1% to 22 million, with US travelers accounting for about a quarter.</p>
                <p className={P}>Chinese and Indian visitors each achieved double-digit growth.</p>
              </div>
              <div className="space-y-5">
                <p className={P}>Most notably, while five-star hotels account for only about 8% of total overnight stays, guests staying there account for 25-30% of Switzerland's total tourism revenue.</p>
                <p className={P}>Markus Berger of Switzerland Tourism comments, "The numbers speak for themselves. The high economic importance justifies our commitment to luxury guests."</p>
              </div>
            </div>

            {/* 05: Differentiation (existing body, unchanged) */}
            <SectionHeading id="s-diff" en="Differentiation">Swiss Differentiation Factors and Competitive Comparison</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>Switzerland's luxury tourism boom is not isolated.</p>
                <p className={P}>Tenerife is repositioning as an exclusive vacation destination for US luxury travelers, while the Maldives, Austria, and Greece offer custom itineraries, private sanctuaries, and standalone wellness complexes.</p>
              </div>
              <div className="space-y-5">
                <p className={P}>However, while Dubai and the Maldives promise opulence, Switzerland offers a unique combination of understated luxury rooted in heritage, wellness, outdoor adventure, and world-class, often "homegrown" service.</p>
                <p className={P}>This combination may continue to be a winning formula for the world's wealthiest travelers.</p>
              </div>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Reported</dt><dd className="text-[#333] mt-0.5">March 3, 2025</dd></div>
                  <div><dt className="text-[#8f7a56]">2024 Overnight Stays</dt><dd className="text-[#333] mt-0.5">42.8M (50-year high)</dd></div>
                  <div><dt className="text-[#8f7a56]">Five-Star Revenue Share</dt><dd className="text-[#333] mt-0.5">25-30% of tourism revenue</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
